import type { ExposureRow } from '@/store/heatmapStore';

export type HeatmapViewPresetKey = 'default' | 'full';

export interface HeatmapViewPreset {
  key: HeatmapViewPresetKey;
  visibleStrikeCount: number | null;
  visibleExpirationCount: number | null;
}

export const DEFAULT_HEATMAP_VIEW_PRESET: HeatmapViewPreset = {
  key: 'default',
  visibleStrikeCount: 92,
  visibleExpirationCount: 5,
};

export const FULL_HEATMAP_VIEW_PRESET: HeatmapViewPreset = {
  key: 'full',
  visibleStrikeCount: null,
  visibleExpirationCount: null,
};

const HEATMAP_VIEW_PRESET_BY_KEY: Record<HeatmapViewPresetKey, HeatmapViewPreset> = {
  default: DEFAULT_HEATMAP_VIEW_PRESET,
  full: FULL_HEATMAP_VIEW_PRESET,
};

export function getHeatmapViewPresetFromSearch(search: string): HeatmapViewPreset {
  const presetKey = new URLSearchParams(search).get('viewPreset');

  if (presetKey === 'full') {
    return FULL_HEATMAP_VIEW_PRESET;
  }

  return DEFAULT_HEATMAP_VIEW_PRESET;
}

export function getDisplayRows(
  rows: ExposureRow[],
  spotPrice: number,
  visibleStrikeCount: number | null = DEFAULT_HEATMAP_VIEW_PRESET.visibleStrikeCount,
): ExposureRow[] {
  if (
    rows.length === 0 ||
    !Number.isFinite(spotPrice) ||
    spotPrice <= 0 ||
    visibleStrikeCount == null ||
    visibleStrikeCount >= rows.length
  ) {
    return rows;
  }

  let closestIndex = 0;
  let minDistance = Number.POSITIVE_INFINITY;

  rows.forEach((row, index) => {
    const distance = Math.abs(row.strike - spotPrice);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  let start = Math.max(0, closestIndex - Math.floor(visibleStrikeCount / 2));
  const end = Math.min(rows.length, start + visibleStrikeCount);
  start = Math.max(0, end - visibleStrikeCount);

  return rows.slice(start, end);
}

export function getDisplayExpirations(
  expirations: string[],
  visibleExpirationCount: number | null = DEFAULT_HEATMAP_VIEW_PRESET.visibleExpirationCount,
): string[] {
  if (visibleExpirationCount == null || visibleExpirationCount >= expirations.length) {
    return expirations;
  }

  return expirations.slice(0, visibleExpirationCount);
}

export function getHeatmapViewPresetByKey(presetKey: HeatmapViewPresetKey): HeatmapViewPreset {
  return HEATMAP_VIEW_PRESET_BY_KEY[presetKey];
}
